import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {Search, ArrowRight} from "lucide-react";
import PublicEditorialHeader from "../components/PublicEditorialHeader";
import {API_URL, IMAGE_HOST} from "../constants";

const CATEGORY_COLORS = {
  WAEC: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
  JAMB: "bg-purple-50 text-purple-700 ring-1 ring-purple-200",
  NECO: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  GCE: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  "Study Tips": "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  News: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
};
const DOT_COLORS = {WAEC: "bg-blue-500", JAMB: "bg-purple-500", NECO: "bg-emerald-500", GCE: "bg-amber-500", "Study Tips": "bg-rose-500", News: "bg-slate-400"};

function unwrapList(response) {
  const root = response?.data ?? response;
  const body = root?.data ?? root;
  const items = Array.isArray(body) ? body : body?.items ?? body?.news ?? body?.blogs ?? body?.results ?? [];
  const pagination = body?.pagination ?? root?.pagination ?? {};
  return {items, totalPages: pagination.totalPages ?? pagination.pages ?? body?.totalPages ?? body?.total_pages ?? 1};
}

function categoryOf(article) {
  return article?.category?.name ?? article?.categoryName ?? article?.category_name ?? (typeof article?.category === "string" ? article.category : "");
}
function titleOf(article) { return article?.title ?? article?.name ?? "Untitled story"; }
function slugOf(article) { return article?.slug ?? article?.id ?? article?._id; }
function authorOf(article) {
  const author = article?.author;
  if (typeof author === "string") return author;
  return [author?.firstName ?? author?.first_name, author?.lastName ?? author?.last_name].filter(Boolean).join(" ") || author?.name || article?.authorName || article?.author_name || "Fillop";
}
function contentOf(article) { return String(article?.content ?? article?.body ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim(); }
function summaryOf(article, max = 180) { return String(article?.metaDescription ?? article?.excerpt ?? article?.summary ?? contentOf(article)).slice(0, max); }
function imageOf(article) {
  const image = article?.coverImage ?? article?.cover_image ?? article?.imageUrl ?? article?.image_url ?? article?.image;
  if (!image) return "";
  return image.startsWith("http") || image.startsWith("//") ? image : `${IMAGE_HOST || ""}${image.startsWith("/") ? "" : "/"}${image}`;
}
function publishedOf(article) { return article?.publishedAt ?? article?.published_at ?? article?.published_date ?? article?.publishAt ?? article?.createdAt ?? article?.created_at; }
function dateLabel(value) { return value ? new Date(value).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"}) : ""; }
function readingTime(article) { return article?.readingTime ?? article?.readTime ?? article?.read_time ?? `${Math.max(1, Math.ceil(contentOf(article).split(/\s+/).filter(Boolean).length / 220))} min read`; }

function CategoryBadge({category, onDark = false}) {
  if (!category) return null;
  if (onDark) return <span className="mb-3 inline-block rounded-full border border-white/20 bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-sm">{category}</span>;
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${CATEGORY_COLORS[category] || "bg-slate-100 text-slate-600"}`}>{category}</span>;
}

function FeaturedStory({article}) {
  const image = imageOf(article);
  const slug = slugOf(article);
  return (
    <Link to={`/blog/${slug}`} state={{from: "/blog", article}} className="group block no-underline">
      <div className="relative h-[360px] w-full overflow-hidden rounded-3xl bg-slate-900 sm:h-[450px]">
        {image && <img src={image} alt={titleOf(article)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" onError={(event) => {event.currentTarget.style.display = "none";}} />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 md:p-12">
          <CategoryBadge category={categoryOf(article)} onDark />
          <h2 className="mb-3 max-w-4xl font-bold leading-tight text-white transition-colors group-hover:text-blue-100" style={{fontFamily: "Georgia, serif", fontSize: "clamp(1.5rem, 3vw, 2.6rem)"}}>{titleOf(article)}</h2>
          <p className="mb-5 line-clamp-2 max-w-3xl text-sm leading-6 text-slate-300 sm:text-base">{summaryOf(article, 240)}</p>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-900 text-[10px] font-black text-white">{authorOf(article).charAt(0).toUpperCase()}</span>
            <span className="text-xs font-medium text-slate-300">{authorOf(article)}</span><span className="text-slate-600">·</span>
            <span className="text-xs text-slate-400">{readingTime(article)}</span>
            {publishedOf(article) && <><span className="text-slate-600">·</span><time className="text-xs text-slate-400" dateTime={publishedOf(article)}>{dateLabel(publishedOf(article))}</time></>}
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 transition-colors group-hover:text-blue-300">Read <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function StoryCard({article}) {
  const image = imageOf(article);
  const author = authorOf(article);
  return (
    <Link to={`/blog/${slugOf(article)}`} state={{from: "/blog", article}} className="group block h-full no-underline">
      <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_-4px_rgba(0,0,0,0.07)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.14)]">
        {image ? <div className="h-40 shrink-0 overflow-hidden bg-slate-100"><img src={image} alt={titleOf(article)} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" onError={(event) => {event.currentTarget.parentElement.style.display = "none";}} /></div> : <div className="flex h-48 shrink-0 items-center justify-center bg-gradient-to-br from-slate-100 to-slate-50"><span className="text-5xl font-black text-slate-200">F.</span></div>}
        <div className="flex flex-1 flex-col p-5">
          <div className="mb-2"><CategoryBadge category={categoryOf(article)} /></div>
          <h3 className="mb-2 line-clamp-2 text-[15px] font-bold leading-snug text-slate-900 transition-colors group-hover:text-blue-900" style={{fontFamily: "Georgia, serif"}}>{titleOf(article)}</h3>
          <p className="mb-4 line-clamp-2 text-xs leading-relaxed text-slate-500">{summaryOf(article, 120)}</p>
          <div className="mt-auto flex items-center gap-2">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-900 text-[9px] font-black text-white">{author.charAt(0).toUpperCase()}</span>
            <span className="truncate text-[11px] font-medium text-slate-400">{author}</span><span className="text-[10px] text-slate-300">·</span>
            <span className="shrink-0 text-[11px] text-slate-400">{readingTime(article)}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}

function BlogFooter() {
  return <footer className="mt-24 border-t border-slate-100 bg-white"><div className="mx-auto flex min-h-14 max-w-7xl flex-col items-center justify-between gap-2 px-6 py-3 text-xs text-slate-400 sm:flex-row"><Link to="/blog" className="text-lg font-extrabold text-blue-900 no-underline">Fillop <span className="text-orange-500">Blog</span></Link><span>© {new Date().getFullYear()} FillOp. All rights reserved.</span></div></footer>;
}

function StorySkeleton() {
  return <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white"><div className="h-40 animate-pulse bg-slate-100" /><div className="space-y-3 p-5"><div className="h-3 w-16 animate-pulse rounded bg-slate-100" /><div className="h-5 w-full animate-pulse rounded bg-slate-100" /><div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" /><div className="mt-4 h-3 w-1/3 animate-pulse rounded bg-slate-100" /></div></div>;
}

export default function News() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(["All", "WAEC", "JAMB", "NECO", "GCE", "Study Tips", "News"]);
  const [category, setCategory] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/public/news/categories`)
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((response) => {
        const root = response?.data ?? response;
        const payload = root?.data ?? root;
        const list = Array.isArray(payload) ? payload : payload?.categories ?? payload?.items ?? [];
        const names = list.map((item) => typeof item === "string" ? item : item.name ?? item.title).filter(Boolean);
        if (active && names.length) setCategories(["All", ...names]);
      }).catch(() => {});
    return () => {active = false;};
  }, []);

  const load = useCallback(async (selectedCategory, pageNumber, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError("");
    try {
      const query = new URLSearchParams({page: String(pageNumber), limit: "9", sort: "newest"});
      if (selectedCategory !== "All") query.set("category", selectedCategory);
      const response = await fetch(`${API_URL}/api/public/news?${query}`);
      if (!response.ok) throw new Error(`Could not load blog posts (${response.status}).`);
      const result = unwrapList(await response.json());
      setItems((current) => append ? [...current, ...result.items] : result.items);
      setHasMore(pageNumber < Number(result.totalPages || 1) || result.items.length === 9);
      setPage(pageNumber);
    } catch (err) {
      setError(err.message || "Could not load blog posts.");
      if (!append) setItems([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { load(category, 1, false); }, [category, load]);
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSearch(searchText.trim().toLowerCase()), 300);
    return () => clearTimeout(timer.current);
  }, [searchText]);

  const matchingItems = useMemo(() => search ? items.filter((item) => titleOf(item).toLowerCase().includes(search) || contentOf(item).toLowerCase().includes(search) || categoryOf(item).toLowerCase().includes(search)) : items, [items, search]);
  const featured = matchingItems[0];
  const stories = matchingItems.slice(1);

  return <div className="min-h-screen bg-slate-50">
    <PublicEditorialHeader />
    <section className="border-b border-slate-100 bg-white">
      <div className="mx-auto flex max-w-7xl flex-col-reverse items-center justify-between gap-5 px-6 pb-8 pl-8 pt-2 md:flex-row">
        <div className="w-full">
          <h1 className="mb-2 mt-5 text-[1.7rem] font-extrabold text-slate-900 md:text-5xl">Blog &amp; Latest News</h1>
        </div>
        <label className="relative mt-6 inline-block w-full self-start md:w-[400px] md:shrink-0">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="search" value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search articles…" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
        </label>
      </div>
    </section>

    <main className="mx-auto max-w-7xl px-6 pb-10 pt-6">
      <div className="mb-7 flex gap-2 overflow-x-auto border-b border-slate-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {categories.map((item) => <button key={item} type="button" onClick={() => setCategory(item)} className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold transition ${category === item ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-900"}`}>{item}</button>)}
      </div>

      {error ? <div className="rounded-2xl border border-red-100 bg-white px-6 py-12 text-center"><p className="mb-4 text-sm font-medium text-red-500">{error}</p><button type="button" onClick={() => load(category, 1)} className="rounded-xl bg-blue-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-blue-800">Try again</button></div>
        : loading ? <div className="space-y-7"><div className="h-[360px] animate-pulse rounded-3xl bg-slate-100 sm:h-[450px]" /><div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{Array.from({length: 6}, (_, index) => <StorySkeleton key={index} />)}</div></div>
          : matchingItems.length === 0 ? <div className="rounded-2xl border border-slate-100 bg-white px-6 py-16 text-center text-sm text-slate-500">No posts found.</div>
            : <div className="space-y-7">
              {featured && <FeaturedStory article={featured} />}
              {stories.length > 0 && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{stories.map((story) => <StoryCard key={story.id ?? story._id ?? story.slug} article={story} />)}</div>}
              {hasMore && !search && <div className="flex justify-center pt-4"><button type="button" onClick={() => load(category, page + 1, true)} disabled={loadingMore} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50">{loadingMore ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-blue-800" /> Loading…</> : "Load more posts"}</button></div>}
            </div>}
    </main>
    <BlogFooter />
  </div>;
}
